import { useState, useEffect, Fragment } from 'react'
import { apiClient } from '../services/api'
import { useAuthStore } from '../services/authStore'
import { CheckCircle2, XCircle, RefreshCcw, Plus, Trash2, Zap } from 'lucide-react'
import { useTranslation } from '../contexts/TranslationContext'
import { formatDateTime } from '../utils/date'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'

function normalizeEngineConfig(engine) {
  // Keep older saved engine rows working when the new opt-in thinking flag is missing.
  return {
    ...engine,
    active: Boolean(engine?.active),
    fallback: Boolean(engine?.fallback),
    skip_ssl_verify: Boolean(engine?.skip_ssl_verify),
    thinking: Boolean(engine?.thinking),
  }
}

const OCRSettings = () => {
  const [settings, setSettings] = useState([])
  const [llmEngines, setLlmEngines] = useState([])
  const [llmPrimaryEngine, setLlmPrimaryEngine] = useState('')
  const [llmFallbackEngine, setLlmFallbackEngine] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [checking, setChecking] = useState(false)
  const [message, setMessage] = useState('')
  const [healthStatus, setHealthStatus] = useState(null)
  const [testLog, setTestLog] = useState('')
  const [engineHealthStatus, setEngineHealthStatus] = useState({})

  const hiddenSettingKeys = [
    'ocr_api_url',
    'ocr_api_key',
    'ocr_model',
    'ocr_temperature',
    'ocr_max_tokens',
    'ocr_skip_ssl_verify',
    'llm_engines',
    'llm_fallback_engine',
    'llm_primary_engine',
    'llm_primary_engine_fast',
    'llm_fallback_engine_fast',
    'llm_primary_engine_slow',
    'llm_fallback_engine_slow',
    'llm_primary_engine_translate',
    'llm_fallback_engine_translate',
    'llm_primary_engine_qrcode',
    'llm_fallback_engine_qrcode',
    'llm_primary_engine_speech',
    'llm_fallback_engine_speech',
    'currency_exchange_rates',
    'ocr_products_prompt',
    'pre_ocr_enabled',
    'pre_ocr_deskew_enabled',
    'pre_ocr_crop_enabled',
    'pre_ocr_denoise_enabled',
    'pre_ocr_perspective_enabled',
    'pre_ocr_threshold',
    'ocr_image_max_width',
    'ocr_image_max_payload_bytes',
  ]
  const preOcrKeys = [
    'pre_ocr_enabled',
    'pre_ocr_deskew_enabled',
    'pre_ocr_crop_enabled',
    'pre_ocr_denoise_enabled',
    'pre_ocr_perspective_enabled',
    'pre_ocr_threshold',
  ]

  const engineTestTtlMs = 60 * 1000

  useEffect(() => {
    loadSettings()
    checkHealth()
  }, [])

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/llm-settings')
      const s = response.data.settings
      setSettings(s)

      const llmItem = s.find(item => item.key === 'llm_engines')
      const fallbackItem = s.find(item => item.key === 'llm_fallback_engine')
      const primaryItem = s.find(item => item.key === 'llm_primary_engine')
      if (llmItem && llmItem.value) {
        try {
          const parsed = typeof llmItem.value === 'string' ? JSON.parse(llmItem.value) : llmItem.value
          setLlmEngines(Array.isArray(parsed) ? parsed.map(normalizeEngineConfig) : [])
        } catch {
          setLlmEngines([])
        }
      }
      if (fallbackItem) {
        setLlmFallbackEngine(fallbackItem.value || '')
      }
      if (primaryItem) {
        setLlmPrimaryEngine(primaryItem.value || '')
      }
    } catch (error) {
      setMessage(t('Failed to load LLM settings; please configure manually', 'Failed to load LLM settings; please configure manually'))
      setSettings([
        { key: 'ocr_enabled', value: false, type: 'boolean', description: 'Enable LLM processing' },
        { key: 'ocr_api_url', value: '', type: 'string', description: 'OCR API URL' },
        { key: 'ocr_api_key', value: '', type: 'string', description: 'OCR API key' },
        { key: 'ocr_skip_ssl_verify', value: false, type: 'boolean', description: 'Skip SSL verification (for self-signed certs)' },
        { key: 'ocr_prompt', value: `Please analyze this receipt image and extract the following information in JSON format:\n\n{\n  "merchant": "Store/restaurant name",\n  "amount": Total amount (number only, e.g., 25.50),\n  "date": "YYYY-MM-DD format",\n  "items": [\n    {"description": "Item name", "price": number}\n  ],\n  "subtotal": number or null,\n  "tax": number or null,\n  "raw_text": "All visible text from receipt"\n}\n\nRules:\n- Merchant: Extract the store/restaurant name from the top of receipt\n- Amount: Find TOTAL or GRAND TOTAL (ignore subtotal)\n- Date: Extract in YYYY-MM-DD format\n- Items: List products with prices (only items with prices)\n- Include raw_text with all visible text\n- If a field is unclear, set to null\n- Return ONLY valid JSON, no explanations\n`, type: 'string', description: 'OCR prompt for receipt extraction' },
      ])
    } finally {
      setLoading(false)
    }
  }

  const checkHealth = async () => {
    try {
      const response = await apiClient.get('/ocr/health')
      setHealthStatus(response.data)
      setTestLog('')
    } catch (error) {
      setHealthStatus({ healthy: false, error: 'Failed to check health', connection: { error: error.message } })
      setTestLog('Failed to get health status: ' + (error?.message || 'Unknown error'))
    }
  }

  const handleTestConnection = async () => {
    setChecking(true)
    setMessage('')
    setTestLog('')

    try {
      const response = await apiClient.get('/ocr/health')
      const data = response.data
      setHealthStatus(data)

      if (data.healthy) {
        setMessage(t('Connection test successful', 'Connection test successful'))
        setTestLog(`Connected to LM Studio at ${data.api_url} (model=${data.model})`)
      } else {
        const errorDetail = data.connection?.error || data.error || 'Unknown error'
        const statusCode = data.connection?.status_code ? `, status_code=${data.connection.status_code}` : ''
        setMessage(t('Connection test failed', 'Connection test failed'))
        setTestLog(`Not connected. API URL=${data.api_url}, model=${data.model}${statusCode}. Error: ${errorDetail}`)
      }
    } catch (error) {
      setMessage(t('Connection test failed', 'Connection test failed'))
      setTestLog('Not connected. Error: ' + (error?.message || 'Unknown error'))
    } finally {
      setChecking(false)
    }
  }

  const handleTestEngine = async (engine) => {
    const existing = engineHealthStatus[engine.id]
    if (existing?.lastChecked && Date.now() - existing.lastChecked < engineTestTtlMs) {
      return
    }

    setEngineHealthStatus((prev) => ({
      ...prev,
      [engine.id]: { loading: true, status: null, error: null, lastChecked: null },
    }))

    try {
      const response = await apiClient.post('/llm-settings/engine-health', { engine })
      setEngineHealthStatus((prev) => ({
        ...prev,
        [engine.id]: {
          loading: false,
          status: response.data,
          error: null,
          lastChecked: Date.now(),
        },
      }))
    } catch (error) {
      setEngineHealthStatus((prev) => ({
        ...prev,
        [engine.id]: {
          loading: false,
          status: null,
          error: error?.response?.data?.message || error?.message || 'Failed to test engine',
          lastChecked: Date.now(),
        },
      }))
    }
  }

  const handleSettingChange = (key, value, type) => {
    if (type === 'json') {
      const trimmed = value.trim()
      setSettings(prev => prev.map(setting =>
        setting.key === key
          ? { ...setting, value: trimmed }
          : setting
      ))
      return
    }

    setSettings(prev => prev.map(setting =>
      setting.key === key
        ? { ...setting, value: type === 'boolean' ? value : value }
        : setting
    ))
  }

  const toggleEngineActive = (engineId) => {
    setLlmEngines((prev) => prev.map((engine) => engine.id === engineId ? { ...engine, active: !engine.active } : engine))
  }

  const setFallbackEngine = (engineId) => {
    setLlmFallbackEngine(engineId)
  }

  const setPrimaryEngine = (engineId) => {
    setLlmPrimaryEngine(engineId)
  }

  const addEngine = () => {
    const engineId = `engine-${Date.now()}`
    setLlmEngines((prev) => [...prev, { id: engineId, name: 'New LLM', active: false, fallback: false, api_url: '', api_key: '', model: '', prompt: '', product_prompt: '', qrcode_prompt: '', speech_prompt: '', product_translation_prompt: '', skip_ssl_verify: false, thinking: false }])
  }

  const removeEngine = (engineId) => {
    setLlmEngines((prev) => prev.filter((engine) => engine.id !== engineId))
    if (llmFallbackEngine === engineId) {
      setLlmFallbackEngine('')
    }
  }

  const updateEngine = (engineId, field, value) => {
    setLlmEngines((prev) => prev.map((engine) => engine.id === engineId ? { ...engine, [field]: value } : engine))
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage('')

    try {
      let payloadSettings = settings
        .filter((setting) => !hiddenSettingKeys.includes(setting.key) || preOcrKeys.includes(setting.key))
        .map((setting) => {
          if (setting.type === 'json') {
            if (typeof setting.value === 'string') {
              try {
                return {
                  ...setting,
                  value: JSON.parse(setting.value || '{}'),
                }
              } catch {
                throw new Error(`Invalid JSON for setting ${setting.key}`)
              }
            }

            return setting
          }

          return setting
        })

      // Update llm engine list and fallback/primary from state to ensure explicit control
      payloadSettings = payloadSettings.filter((setting) => setting.key !== 'llm_engines' && setting.key !== 'llm_fallback_engine' && setting.key !== 'llm_primary_engine')

      payloadSettings.push({
        key: 'llm_engines',
        value: llmEngines,
        type: 'json',
        description: 'List of LLM engine configs with active/fallback flags',
      })

      payloadSettings.push({
        key: 'llm_fallback_engine',
        value: llmFallbackEngine,
        type: 'string',
        description: 'Fallback engine ID',
      })

      payloadSettings.push({
        key: 'llm_primary_engine',
        value: llmPrimaryEngine,
        type: 'string',
        description: 'Primary engine ID',
      })


      await apiClient.put('/llm-settings', { settings: payloadSettings })
      setMessage(t('LLM settings saved successfully!', 'LLM settings saved successfully!'))
      await checkHealth() // Re-check health after saving
    } catch (error) {
      setMessage(error.message || t('Failed to save LLM settings', 'Failed to save LLM settings'))
    } finally {
      setSaving(false)
    }
  }

  const renderSettingInput = (setting) => {
    const { key, value, type, description } = setting

    switch (type) {
      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={key}
              checked={value || false}
              onChange={(e) => handleSettingChange(key, e.target.checked, type)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-outline rounded"
            />
            <label htmlFor={key} className="ml-2 block text-sm text-foreground">
              {description}
            </label>
          </div>
        )

      case 'number':
        return (
          <div>
            <label htmlFor={key} className="block text-sm font-medium text-foreground">
              {description}
            </label>
            <input
              type="number"
              id={key}
              value={value || ''}
              onChange={(e) => handleSettingChange(key, parseFloat(e.target.value), type)}
              className="mt-1 block w-full border-outline rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              step="0.1"
            />
          </div>
        )

      default:
        if (key === 'ocr_field_mappings') {
          const mappingCount = Array.isArray(value) ? value.length : 0
          return (
            <div className="space-y-3">
              <div>
                <label htmlFor={key} className="block text-sm font-medium text-foreground">
                  {description}
                </label>
                <p className="mt-1 text-sm text-muted-foreground">Field mapping rules are currently managed by backend configuration.</p>
              </div>
              <div className="rounded-xl border border-outline bg-surface-elevated p-4 text-sm text-foreground">
                {mappingCount > 0
                  ? `${mappingCount} saved mapping rule${mappingCount === 1 ? '' : 's'}.`
                  : 'No field mapping rules configured yet.'}
              </div>
            </div>
          )
        }

        if (key === 'ocr_prompt') {
          return (
            <div>
              <label htmlFor={key} className="block text-sm font-medium text-foreground">
                {description}
              </label>
              <textarea
                id={key}
                value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
                onChange={(e) => handleSettingChange(key, e.target.value, type)}
                rows={10}
                className="mt-1 block w-full min-h-[250px] border-outline rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
            </div>
          )
        }

        return (
          <div>
            <label htmlFor={key} className="block text-sm font-medium text-foreground">
              {description}
            </label>
            <input
              type="text"
              id={key}
              value={value || ''}
              onChange={(e) => handleSettingChange(key, e.target.value, type)}
              className="mt-1 block w-full border-outline rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        )
    }
  }

  const getSetting = (key) => settings.find((setting) => setting.key === key)
  const getSettingBoolean = (key, fallback = false) => {
    const setting = getSetting(key)
    if (!setting) return fallback
    if (typeof setting.value === 'boolean') return setting.value
    if (typeof setting.value === 'string') return setting.value.trim().toLowerCase() === 'true'
    if (typeof setting.value === 'number') return setting.value !== 0
    return fallback
  }
  const getSettingString = (key, fallback = '') => {
    const setting = getSetting(key)
    if (!setting || setting.value == null) return fallback
    return String(setting.value)
  }

  const preOcrEnabled = getSettingBoolean('pre_ocr_enabled', false)
  const preOcrDeskewEnabled = getSettingBoolean('pre_ocr_deskew_enabled', true)
  const preOcrCropEnabled = getSettingBoolean('pre_ocr_crop_enabled', true)
  const preOcrDenoiseEnabled = getSettingBoolean('pre_ocr_denoise_enabled', true)
  const preOcrPerspectiveEnabled = getSettingBoolean('pre_ocr_perspective_enabled', false)
  const preOcrThreshold = getSettingString('pre_ocr_threshold', '')

  const { user } = useAuthStore()
  const { t } = useTranslation()

  if (!user?.is_admin) {
    return (
      <PageShell>
        <Card>
          <h3 className="text-lg font-semibold">{t('Access Denied', 'Access Denied')}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{t('You are not authorised to view LLM settings.', 'You are not authorised to view LLM settings.')}</p>
        </Card>
      </PageShell>
    )
  }

  if (loading) {
    return (
      <PageShell><LoadingState /></PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={t('OCR & LLM settings', 'OCR & LLM settings')}
        subtitle={t('Control all LLM engine configurations, test connectivity, and select primary/fallback providers.', 'Control all LLM engine configurations, test connectivity, and select primary/fallback providers.')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleTestConnection} disabled={checking}>
              <RefreshCcw className="h-4 w-4" />
              {checking ? t('Testing...', 'Testing...') : t('Test Default Connection', 'Test Default Connection')}
            </Button>
            <Button variant="secondary" onClick={() => llmEngines.forEach((engine) => handleTestEngine(engine))} disabled={llmEngines.length === 0}>
              <Zap className="h-4 w-4" />
              {t('Test All Engines', 'Test All Engines')}
            </Button>
            <Button variant="ghost" onClick={addEngine}>
              <Plus className="h-4 w-4" />
              {t('Add Engine', 'Add Engine')}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <aside className="rounded-xl border border-outline bg-surface p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t('Status Summary', 'Status Summary')}</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-md bg-surface-elevated px-3 py-2 text-xs">
              <span>{t('Default LLM health', 'Default LLM health')}</span>
              {healthStatus?.healthy ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-green-700"><CheckCircle2 className="h-3.5 w-3.5" /> {t('Healthy', 'Healthy')}</span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-red-700"><XCircle className="h-3.5 w-3.5" /> {t('Unhealthy', 'Unhealthy')}</span>
              )}
            </div>
            <div className="rounded-md bg-surface-elevated p-2 text-xs text-muted-foreground">
              <div>API URL: <span className="font-medium text-foreground">{healthStatus?.api_url ?? '-'}</span></div>
              <div>Model: <span className="font-medium text-foreground">{healthStatus?.model ?? '-'}</span></div>
              <div>{t('Last log:', 'Last log:')} <span className="font-medium text-foreground">{testLog ? testLog.split('\n')[0] : t('No tests run', 'No tests run')}</span></div>
            </div>
          </div>
        </aside>

        <section className="lg:col-span-2">
          <div className="rounded-xl border border-outline bg-surface p-5 shadow-sm">
            <h3 className="text-lg font-semibold text-foreground">{t('LLM Engines', 'LLM Engines')}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t('Add or edit engines, mark as active and maintain provider settings.', 'Add or edit engines, mark as active and maintain provider settings.')}</p>

            <div className="mt-4 overflow-x-auto">
              {llmEngines.length === 0 ? (
                <div className="rounded-md border border-dashed border-outline bg-surface-elevated p-4 text-center text-sm text-muted-foreground">
                  No LLM engines configured yet. Click {'"'}Add Engine{'"'} to create one and configure API details.
                </div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-surface-elevated text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">ID</th>
                      <th className="px-3 py-2">{t('Name', 'Name')}</th>
                      <th className="px-3 py-2 text-center">{t('Active', 'Active')}</th>
                      <th className="px-3 py-2 text-center">{t('Actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {llmEngines.map((engine) => (
                      <Fragment key={engine.id}>
                      <tr className="hover:bg-surface-elevated">
                        <td className="px-3 py-2 align-top">{engine.id}</td>
                        <td className="px-3 py-2">
                          <input
                            value={engine.name}
                            onChange={(e) => updateEngine(engine.id, 'name', e.target.value)}
                            className="w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={engine.active}
                            onChange={() => toggleEngineActive(engine.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => handleTestEngine(engine)}
                            disabled={engineHealthStatus[engine.id]?.loading}
                            className="mr-1 inline-flex items-center rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            <RefreshCcw className="mr-1 h-3.5 w-3.5" />
                            {engineHealthStatus[engine.id]?.loading ? t('Testing', 'Testing') : t('Test', 'Test')}
                          </button>
                          <button
                            onClick={() => removeEngine(engine.id)}
                            className="inline-flex items-center rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            {t('Remove', 'Remove')}
                          </button>
                        </td>
                      </tr>
                      <tr className="bg-surface-elevated">
                        <td colSpan={4} className="px-3 py-3">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">{t('API URL', 'API URL')}</label>
                              <input
                                className="mt-1 w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={engine.api_url || ''}
                                onChange={(e) => updateEngine(engine.id, 'api_url', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">{t('API Key', 'API Key')}</label>
                              <input
                                className="mt-1 w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={engine.api_key || ''}
                                onChange={(e) => updateEngine(engine.id, 'api_key', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">{t('Model', 'Model')}</label>
                              <input
                                className="mt-1 w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={engine.model || ''}
                                onChange={(e) => updateEngine(engine.id, 'model', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">{t('Temperature', 'Temperature')}</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="1"
                                className="mt-1 w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={engine.temperature ?? 0.3}
                                onChange={(e) => updateEngine(engine.id, 'temperature', parseFloat(e.target.value))}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">{t('Max tokens', 'Max tokens')}</label>
                              <input
                                type="number"
                                className="mt-1 w-full rounded-md border border-outline px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                value={engine.max_tokens ?? 1000}
                                onChange={(e) => updateEngine(engine.id, 'max_tokens', parseInt(e.target.value, 10))}
                              />
                            </div>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={engine.thinking || false}
                                onChange={(e) => updateEngine(engine.id, 'thinking', e.target.checked)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-xs text-muted-foreground">{t('Thinking', 'Thinking')}</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={engine.skip_ssl_verify || false}
                                onChange={(e) => updateEngine(engine.id, 'skip_ssl_verify', e.target.checked)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-xs text-muted-foreground">{t('Skip SSL verify', 'Skip SSL verify')}</span>
                            </label>
                          </div>

                          <div className="mt-2 text-xs">
                            {engineHealthStatus[engine.id] && engineHealthStatus[engine.id].status ? (
                              <div className={`rounded-md px-2 py-1 ${engineHealthStatus[engine.id].status.healthy ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                                <strong>{t('Test result:', 'Test result:')}</strong> {engineHealthStatus[engine.id].status.healthy ? t('Healthy', 'Healthy') : t('Unhealthy', 'Unhealthy')}
                                <span className="ml-2">{engineHealthStatus[engine.id].status.error || t('Connected', 'Connected')}</span>
                                <div className="mt-1 text-muted-foreground">Last checked: {formatDateTime(engineHealthStatus[engine.id].lastChecked)}</div>
                              </div>
                            ) : engineHealthStatus[engine.id] && engineHealthStatus[engine.id].error ? (
                              <div className="rounded-md bg-yellow-50 px-2 py-1 text-yellow-700">
                                Error: {engineHealthStatus[engine.id].error}
                                <div className="text-muted-foreground">Last attempted: {engineHealthStatus[engine.id].lastChecked ? formatDateTime(engineHealthStatus[engine.id].lastChecked) : 'N/A'}</div>
                              </div>
                            ) : null}
                          </div>

                          <div className="mt-2 rounded-md bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
                            {t('Queue-specific prompt editing was moved to /llm-settings.', 'Queue-specific prompt editing was moved to /llm-settings.')}
                          </div>
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>

          <div className="mb-6 rounded-xl border border-outline bg-surface p-5 shadow-sm">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-foreground">{t('Primary LLM engine', 'Primary LLM engine')}</label>
                <select
                  value={llmPrimaryEngine}
                  onChange={(e) => setPrimaryEngine(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-outline px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">{t('-- Select Primary Engine --', '-- Select Primary Engine --')}</option>
                  {llmEngines.map((engine) => (
                    <option key={engine.id} value={engine.id}>{engine.name || engine.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground">{t('Fallback LLM engine', 'Fallback LLM engine')}</label>
                <select
                  value={llmFallbackEngine}
                  onChange={(e) => setFallbackEngine(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-outline px-2 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">{t('-- Select Fallback Engine --', '-- Select Fallback Engine --')}</option>
                  {llmEngines.map((engine) => (
                    <option key={engine.id} value={engine.id}>{engine.name || engine.id}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {preOcrKeys.every((key) => Boolean(getSetting(key))) && (
            <div className="mb-6 rounded-xl border border-outline bg-surface p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-foreground">{t('Pre-OCR Image Pipeline', 'Pre-OCR Image Pipeline')}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('Safe preprocessing before OCR to improve readability on skewed, noisy, or low-contrast receipts.', 'Safe preprocessing before OCR to improve readability on skewed, noisy, or low-contrast receipts.')}
              </p>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex items-start gap-3 rounded-lg border border-outline bg-surface-elevated px-3 py-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                    checked={preOcrEnabled}
                    onChange={(e) => handleSettingChange('pre_ocr_enabled', e.target.checked, 'boolean')}
                  />
                  <span>
                    <span className="block text-sm font-medium text-foreground">{t('Enable Pre-OCR', 'Enable Pre-OCR')}</span>
                    <span className="block text-xs text-muted-foreground">{t('Turns on the full preprocessing chain before image OCR calls.', 'Turns on the full preprocessing chain before image OCR calls.')}</span>
                  </span>
                </label>

                <div className="rounded-lg border border-outline bg-surface-elevated px-3 py-3">
                  <label className="block text-sm font-medium text-foreground">{t('Threshold (1-255)', 'Threshold (1-255)')}</label>
                  <input
                    type="number"
                    min="1"
                    max="255"
                    placeholder={t('Auto', 'Auto')}
                    value={preOcrThreshold}
                    onChange={(e) => handleSettingChange('pre_ocr_threshold', e.target.value, 'string')}
                    className="mt-2 w-full rounded-md border border-outline px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">{t('Example: 170 for faded thermal paper; empty keeps automatic behavior.', 'Example: 170 for faded thermal paper; empty keeps automatic behavior.')}</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="flex items-start gap-3 rounded-lg border border-outline px-3 py-3">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500" checked={preOcrDeskewEnabled} onChange={(e) => handleSettingChange('pre_ocr_deskew_enabled', e.target.checked, 'boolean')} />
                  <span><span className="block text-sm font-medium text-foreground">{t('Deskew / Rotation', 'Deskew / Rotation')}</span><span className="block text-xs text-muted-foreground">{t('Corrects orientation for tilted camera shots.', 'Corrects orientation for tilted camera shots.')}</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-outline px-3 py-3">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500" checked={preOcrCropEnabled} onChange={(e) => handleSettingChange('pre_ocr_crop_enabled', e.target.checked, 'boolean')} />
                  <span><span className="block text-sm font-medium text-foreground">{t('Document Crop', 'Document Crop')}</span><span className="block text-xs text-muted-foreground">{t('Removes border/background noise around the receipt edges.', 'Removes border/background noise around the receipt edges.')}</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-outline px-3 py-3">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500" checked={preOcrDenoiseEnabled} onChange={(e) => handleSettingChange('pre_ocr_denoise_enabled', e.target.checked, 'boolean')} />
                  <span><span className="block text-sm font-medium text-foreground">{t('Denoise + Contrast', 'Denoise + Contrast')}</span><span className="block text-xs text-muted-foreground">{t('Improves readability on grainy/low-light photos.', 'Improves readability on grainy/low-light photos.')}</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-outline px-3 py-3">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500" checked={preOcrPerspectiveEnabled} onChange={(e) => handleSettingChange('pre_ocr_perspective_enabled', e.target.checked, 'boolean')} />
                  <span><span className="block text-sm font-medium text-foreground">{t('Perspective Correction', 'Perspective Correction')}</span><span className="block text-xs text-muted-foreground">{t('Helps when receipt is photographed at an angle.', 'Helps when receipt is photographed at an angle.')}</span></span>
                </label>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {settings
              .filter((setting) => !hiddenSettingKeys.includes(setting.key))
              .map((setting) => (
                <div key={setting.key} className="border-b border-outline pb-4">
                  {renderSettingInput(setting)}
                </div>
              ))}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" />
              {saving ? t('Saving...', 'Saving...') : t('Save Settings', 'Save Settings')}
            </button>
            <button
              onClick={checkHealth}
              className="inline-flex items-center gap-2 rounded-md border border-outline bg-surface px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-surface-elevated"
            >
              <RefreshCcw className="h-4 w-4" />
              {t('Re-check health', 'Re-check health')}
            </button>
          </div>

          {message && (
            <div
              className={`mt-4 rounded-md px-4 py-3 text-sm ${
                message.includes('successfully') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {message}
            </div>
          )}
      </section>
    </div>
    </PageShell>
  )
}

export default OCRSettings

